import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ColumnConfig, ColumnGroup } from './types';
import {
  getStoredColumnPrefs,
  setStoredColumnPrefs,
  type ColumnPrefs,
} from './columnPrefsStorage';

/**
 * Synthetic / caller-pinned columns are excluded from management entirely:
 * never in the Columns panel, never hideable, always rendered before every
 * manageable column in their original relative order. The `__` prefix
 * covers synthetic columns (e.g. the entity grid's selection checkbox)
 * even when the caller didn't set `managed: false`.
 */
export function isColumnPinned(col: ColumnConfig): boolean {
  return col.managed === false || col.data.startsWith('__');
}

/**
 * Sticky columns aren't hideable (hiding one would break the freeze pane's
 * contiguous-from-left assumption in `computeStickyMeta`); required columns
 * aren't hideable so validation errors can't be hidden out of sight.
 */
export function isColumnHideable(col: ColumnConfig): boolean {
  return !isColumnPinned(col) && !col.sticky && !col.required;
}

export interface UseColumnPrefsParams {
  columns: ColumnConfig[];
  /** Prefs persist under `editable-table:${storageKey}:column-prefs`. */
  storageKey?: string;
  /** The `enableColumnManagement` prop. */
  enabled?: boolean;
  /** Column management is disabled while groups exist — reordering would
   *  break the colSpan'd group headers' contiguity. */
  columnGroups?: ColumnGroup[];
}

export interface UseColumnPrefsResult {
  /**
   * What the table should render: all visible columns (pinned first, then
   * sticky, then the rest) in the user's order. Identical to `columns`
   * (same reference) when management is off or the user hasn't deviated
   * from defaults.
   */
  managedColumns: ColumnConfig[];
  /**
   * Rows for the Columns panel: every non-pinned column in the user's
   * order, including hidden ones.
   */
  manageableColumns: ColumnConfig[];
  /** `col.data` keys currently hidden. */
  hiddenSet: ReadonlySet<string>;
  /** False when disabled, no storageKey, or columnGroups present. */
  isManaged: boolean;
  /** Show/hide a column. No-op for non-hideable columns. */
  toggleHidden: (colData: string) => void;
  /**
   * Move a column to `toIndex` within `manageableColumns`. Clamped to the
   * column's partition: sticky columns stay within the leading sticky
   * block, others stay after it.
   */
  moveColumn: (colData: string, toIndex: number) => void;
  /** Restore default order + visibility and remove the stored prefs. */
  reset: () => void;
}

interface Resolution {
  managedColumns: ColumnConfig[];
  manageableColumns: ColumnConfig[];
  /** `manageableColumns` as data keys — the shape we persist as `order`. */
  manageableKeys: string[];
  hiddenSet: Set<string>;
  /** Leading sticky-block length within `manageableColumns`. */
  stickyCount: number;
}

/**
 * Reconcile stored prefs against the live column set.
 *
 * Order: stored keys for removed columns are dropped; columns missing from
 * the store are inserted after their nearest preceding default-order
 * neighbor (so a newly defined column lands beside where it was declared,
 * even in a reordered table). Partitions are then enforced:
 * `[pinned (default order), sticky (user order), rest (user order)]`.
 *
 * Hidden: an absent `hidden` field means "apply `defaultHidden` flags"; a
 * present one (even `[]`) is authoritative — a user who revealed a
 * default-hidden column keeps it revealed. Unknown keys are dropped and
 * non-hideable columns force-unhidden (guards a stale/hand-edited store).
 */
function resolve(columns: ColumnConfig[], prefs: ColumnPrefs): Resolution {
  const byKey = new Map(columns.map((c) => [c.data, c]));
  const defaultOrder = columns.map((c) => c.data);

  const reconciled = (prefs.order ?? []).filter((k) => byKey.has(k));
  const present = new Set(reconciled);
  let lastAnchor: string | null = null;
  for (const key of defaultOrder) {
    if (present.has(key)) {
      lastAnchor = key;
      continue;
    }
    const insertAt = lastAnchor === null ? 0 : reconciled.indexOf(lastAnchor) + 1;
    reconciled.splice(insertAt, 0, key);
    present.add(key);
    lastAnchor = key;
  }

  const pinnedKeys = defaultOrder.filter((k) => isColumnPinned(byKey.get(k)!));
  const stickyKeys = reconciled.filter((k) => {
    const col = byKey.get(k)!;
    return !isColumnPinned(col) && col.sticky;
  });
  const normalKeys = reconciled.filter((k) => {
    const col = byKey.get(k)!;
    return !isColumnPinned(col) && !col.sticky;
  });
  const manageableKeys = [...stickyKeys, ...normalKeys];

  const baseHidden =
    prefs.hidden ?? columns.filter((c) => c.defaultHidden).map((c) => c.data);
  const hiddenSet = new Set(
    baseHidden.filter((k) => {
      const col = byKey.get(k);
      return col !== undefined && isColumnHideable(col);
    }),
  );

  const managedColumns = [...pinnedKeys, ...manageableKeys]
    .filter((k) => !hiddenSet.has(k))
    .map((k) => byKey.get(k)!);
  const manageableColumns = manageableKeys.map((k) => byKey.get(k)!);

  return { managedColumns, manageableColumns, manageableKeys, hiddenSet, stickyCount: stickyKeys.length };
}

const arraysEqual = (a: string[], b: string[]) =>
  a.length === b.length && a.every((v, i) => v === b[i]);

const EMPTY_COLUMNS: ColumnConfig[] = [];
const EMPTY_HIDDEN: ReadonlySet<string> = new Set();

/**
 * Column order + visibility state for `EditableTable`'s opt-in column
 * management. Sibling of `useColumnWidths`: hydrates from localStorage,
 * persists on user change after hydration, and normalizes back to "no key"
 * when the user's prefs match the column defaults — so Reset (and an
 * untouched table) leaves no residue.
 */
export function useColumnPrefs({
  columns,
  storageKey,
  enabled = false,
  columnGroups,
}: UseColumnPrefsParams): UseColumnPrefsResult {
  const hasColumnGroups = !!columnGroups && columnGroups.length > 0;
  const isManaged = enabled && !!storageKey && !hasColumnGroups;
  const hydrationKey = isManaged && storageKey ? storageKey : null;

  // Dev guard: `enabled` can be silently ineffective. Warn so a dev who
  // sets the flag on a grouped table (budgets, timesheets) or forgets the
  // storageKey doesn't burn time wondering where the trigger went (mirrors
  // stickyUtils' console.warn convention for config conflicts).
  useEffect(() => {
    if (!enabled) return;
    if (hasColumnGroups) {
      console.warn(
        '[EditableTable] enableColumnManagement is ignored: columnGroups are set '
        + '(reordering would break group contiguity).',
      );
    } else if (!storageKey) {
      console.warn(
        '[EditableTable] enableColumnManagement requires a storageKey — prefs have nowhere to persist; the feature is disabled.',
      );
    }
  }, [enabled, storageKey, hasColumnGroups]);

  const [prefs, setPrefs] = useState<ColumnPrefs>(() =>
    hydrationKey ? getStoredColumnPrefs(hydrationKey) : {},
  );

  // Re-hydrate when the storage key changes on a live instance — same
  // render-phase adjustment as useColumnWidths, so the swapped prefs paint
  // in the same render and the persist effect can't write the old table's
  // prefs under the new key.
  const [hydratedKey, setHydratedKey] = useState(hydrationKey);
  if (hydratedKey !== hydrationKey) {
    setHydratedKey(hydrationKey);
    setPrefs(hydrationKey ? getStoredColumnPrefs(hydrationKey) : {});
  }

  const resolution = useMemo(
    () => (isManaged ? resolve(columns, prefs) : null),
    [isManaged, columns, prefs],
  );

  // Identity passthrough when the resolved set matches the prop exactly —
  // tables that opted in but were never customized render byte-identically.
  const managedColumns = useMemo(() => {
    if (!resolution) return columns;
    const managed = resolution.managedColumns;
    const identical =
      managed.length === columns.length && managed.every((c, i) => c === columns[i]);
    return identical ? columns : managed;
  }, [resolution, columns]);

  // Refs so the mutation callbacks stay referentially stable (panel rows
  // don't re-render on every table render).
  const resolutionRef = useRef(resolution);
  const columnsRef = useRef(columns);
  useEffect(() => {
    resolutionRef.current = resolution;
    columnsRef.current = columns;
  });

  // Persist on user change after hydration. We store the *resolved* values
  // (stale keys already dropped, new columns already placed) so the store
  // self-heals, and omit any field that matches defaults so untouched
  // tables keep no localStorage entry.
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (!hydrationKey) return;
    if (!hydratedRef.current) {
      hydratedRef.current = true;
      return;
    }
    const res = resolve(columnsRef.current, prefs);
    const defaults = resolve(columnsRef.current, {});
    setStoredColumnPrefs(hydrationKey, {
      order: arraysEqual(res.manageableKeys, defaults.manageableKeys)
        ? undefined
        : res.manageableKeys,
      hidden:
        res.hiddenSet.size === defaults.hiddenSet.size &&
        [...res.hiddenSet].every((k) => defaults.hiddenSet.has(k))
          ? undefined
          : [...res.hiddenSet],
    });
  }, [prefs, hydrationKey]);

  const toggleHidden = useCallback((colData: string) => {
    const res = resolutionRef.current;
    if (!res) return;
    const col = columnsRef.current.find((c) => c.data === colData);
    if (!col || !isColumnHideable(col)) return;
    const next = new Set(res.hiddenSet);
    if (next.has(colData)) next.delete(colData);
    else next.add(colData);
    setPrefs((prev) => ({ ...prev, hidden: [...next] }));
  }, []);

  const moveColumn = useCallback((colData: string, toIndex: number) => {
    const res = resolutionRef.current;
    if (!res) return;
    const keys = res.manageableKeys;
    const fromIdx = keys.indexOf(colData);
    if (fromIdx === -1) return; // pinned or unknown
    const col = columnsRef.current.find((c) => c.data === colData)!;
    const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));
    const target = col.sticky
      ? clamp(toIndex, 0, res.stickyCount - 1)
      : clamp(toIndex, res.stickyCount, keys.length - 1);
    if (target === fromIdx) return;
    const next = [...keys];
    next.splice(fromIdx, 1);
    next.splice(target, 0, colData);
    setPrefs((prev) => ({ ...prev, order: next }));
  }, []);

  const reset = useCallback(() => setPrefs({}), []);

  return {
    managedColumns,
    manageableColumns: resolution?.manageableColumns ?? EMPTY_COLUMNS,
    hiddenSet: resolution?.hiddenSet ?? EMPTY_HIDDEN,
    isManaged,
    toggleHidden,
    moveColumn,
    reset,
  };
}
